import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  IconButton,
  TextField,
  Button,
  Collapse,
  Divider,
  Tooltip,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const UNCategorizedId = '__none__';

function groupQueries(savedQueries) {
  const byFolder = {};
  (savedQueries || []).forEach((q) => {
    const key = q.folderId != null ? String(q.folderId) : UNCategorizedId;
    if (!byFolder[key]) byFolder[key] = [];
    byFolder[key].push(q);
  });
  Object.keys(byFolder).forEach((k) => {
    byFolder[k].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || (b.updatedAt || '').localeCompare(a.updatedAt || ''));
  });
  return byFolder;
}

const QueryFolderSidebar = ({
  savedQueries = [],
  selectedQueryId,
  isNewQueryDraft = false,
  onNewQuery,
  onSelectQuery,
  onQueriesChange,
  getAuthHeaders,
  open = true,
}) => {
  const [folders, setFolders] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [newFolderName, setNewFolderName] = useState('');
  const [loading, setLoading] = useState(false);
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);

  const grouped = useMemo(() => groupQueries(savedQueries), [savedQueries]);

  const loadFoldersMutation = useMutation({
    mutationFn: (headers) => axios.get('/api/query-folders', { headers }),
  });

  const loadFolders = async () => {
    const headers = getAuthHeaders?.();
    if (!headers) return;
    setLoading(true);
    try {
      const res = await loadFoldersMutation.mutateAsync(headers);
      if (res.data.success) {
        setFolders(res.data.folders || []);
        const exp = {};
        (res.data.folders || []).forEach((f) => { exp[f.id] = true; });
        exp[UNCategorizedId] = true;
        setExpanded(exp);
      }
    } catch (err) {
      console.warn('No se pudieron cargar carpetas:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) loadFolders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const createFolderMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.post('/api/query-folders/save', body, { headers }),
  });

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    const headers = getAuthHeaders?.();
    if (!headers) return;
    try {
      const res = await createFolderMutation.mutateAsync({ body: { name }, headers });
      if (res.data.success) {
        setNewFolderName('');
        setShowNewFolderInput(false);
        await loadFolders();
      }
    } catch (err) {
      console.error('Error al crear carpeta:', err);
    }
  };

  const deleteFolderMutation = useMutation({
    mutationFn: ({ folderId, headers }) => axios.delete(`/api/query-folders/${folderId}`, { headers }),
  });

  const handleDeleteFolder = async (folderId, e) => {
    e.stopPropagation();
    const headers = getAuthHeaders?.();
    if (!headers) return;
    try {
      await deleteFolderMutation.mutateAsync({ folderId, headers });
      const next = (savedQueries || []).map((q) =>
        q.folderId === folderId ? { ...q, folderId: null } : q
      );
      onQueriesChange?.(next);
      await loadFolders();
    } catch (err) {
      console.error('Error al eliminar carpeta:', err);
    }
  };

  const reorderQueriesMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.post('/api/queries/reorder', body, { headers }),
  });

  const persistReorder = async (items) => {
    const headers = getAuthHeaders?.();
    if (!headers) return;
    try {
      await reorderQueriesMutation.mutateAsync({ body: { items }, headers });
    } catch (err) {
      console.error('Error al reordenar:', err);
    }
  };

  const onDragEnd = (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const queryId = parseInt(draggableId.replace('q-', ''), 10);
    const destFolderId = destination.droppableId === UNCategorizedId ? null : parseInt(destination.droppableId, 10);

    const destKey = destFolderId == null ? UNCategorizedId : String(destFolderId);
    const sourceKey = source.droppableId;

    const destList = [...(grouped[destKey] || [])];
    const sourceList = sourceKey === destKey
      ? destList
      : [...(grouped[sourceKey] || [])];

    const moved = (grouped[sourceKey] || []).find((q) => q.id === queryId);
    if (!moved) return;

    if (sourceKey !== destKey) {
      sourceList.splice(source.index, 1);
    } else {
      sourceList.splice(source.index, 1);
    }

    const updatedMoved = { ...moved, folderId: destFolderId };
    destList.splice(destination.index, 0, updatedMoved);

    const nextQueries = (savedQueries || []).filter((q) => q.id !== queryId);
    destList.forEach((q, idx) => {
      nextQueries.push({ ...q, folderId: destFolderId, sortOrder: idx });
    });
    if (sourceKey !== destKey) {
      sourceList.forEach((q, idx) => {
        const existing = nextQueries.find((x) => x.id === q.id);
        if (existing) existing.sortOrder = idx;
        else nextQueries.push({ ...q, sortOrder: idx });
      });
    }

    onQueriesChange?.(nextQueries);
    persistReorder(nextQueries.map((q, idx) => ({
      id: q.id,
      folderId: q.folderId,
      sortOrder: q.sortOrder != null ? q.sortOrder : idx,
    })));
  };

  const renderQueryList = (list, droppableId) => (
    <Droppable droppableId={droppableId}>
      {(provided) => (
        <List dense disablePadding ref={provided.innerRef} {...provided.droppableProps}>
          {(list || []).map((q, index) => (
            <Draggable key={q.id} draggableId={`q-${q.id}`} index={index}>
              {(dragProvided) => (
                <ListItemButton
                  ref={dragProvided.innerRef}
                  {...dragProvided.draggableProps}
                  {...dragProvided.dragHandleProps}
                  selected={!isNewQueryDraft && selectedQueryId === q.id}
                  onClick={() => onSelectQuery?.(q.id)}
                  sx={{ pl: 3 }}
                >
                  <ListItemText
                    primary={q.name || `Query ${q.id}`}
                    secondary={q.database}
                    primaryTypographyProps={{ noWrap: true, variant: 'body2' }}
                    secondaryTypographyProps={{ noWrap: true, variant: 'caption' }}
                  />
                </ListItemButton>
              )}
            </Draggable>
          ))}
          {provided.placeholder}
        </List>
      )}
    </Droppable>
  );

  const sections = [
    { id: UNCategorizedId, name: 'Sin carpeta', deletable: false },
    ...folders.map((f) => ({ id: String(f.id), name: f.name, folderId: f.id, deletable: true })),
  ];

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Queries guardadas
          </Typography>
          <Tooltip title="Crear una query nueva (elige conexión y BD en el panel central)">
            <Button
              size="small"
              variant="contained"
              startIcon={<NoteAddIcon />}
              onClick={() => onNewQuery?.()}
              sx={{ whiteSpace: 'nowrap', minWidth: 0, px: 1.25 }}
            >
              Nueva query
            </Button>
          </Tooltip>
        </Box>

        {isNewQueryDraft && (
          <ListItemButton
            selected
            sx={{
              mb: 1,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'primary.main',
              bgcolor: 'action.selected',
            }}
            onClick={() => onNewQuery?.()}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <NoteAddIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Query nueva (sin guardar)"
              secondary="Configura origen de datos y escribe SQL o usa la IA"
              primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
              secondaryTypographyProps={{ variant: 'caption' }}
            />
          </ListItemButton>
        )}

        <Divider sx={{ my: 1 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            Carpetas
          </Typography>
          <Tooltip title="Nueva carpeta para organizar queries">
            <IconButton
              size="small"
              onClick={() => setShowNewFolderInput((v) => !v)}
              aria-label="Nueva carpeta"
            >
              <CreateNewFolderIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {showNewFolderInput && (
          <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
            <TextField
              size="small"
              placeholder="Nombre de la carpeta"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              fullWidth
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateFolder();
                if (e.key === 'Escape') {
                  setShowNewFolderInput(false);
                  setNewFolderName('');
                }
              }}
            />
            <IconButton size="small" onClick={handleCreateFolder} disabled={!newFolderName.trim()} color="primary">
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Typography variant="body2" sx={{ p: 2 }} color="text.secondary">Cargando…</Typography>
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            {sections.map((section) => {
              const list = grouped[section.id] || [];
              const isOpen = expanded[section.id] !== false;
              return (
                <Box key={section.id}>
                  <ListItemButton onClick={() => setExpanded((p) => ({ ...p, [section.id]: !isOpen }))}>
                    {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    {isOpen ? <FolderOpenIcon fontSize="small" sx={{ mr: 1 }} /> : <FolderIcon fontSize="small" sx={{ mr: 1 }} />}
                    <ListItemText primary={`${section.name} (${list.length})`} primaryTypographyProps={{ variant: 'body2' }} />
                    {section.deletable && (
                      <IconButton size="small" onClick={(e) => handleDeleteFolder(section.folderId, e)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </ListItemButton>
                  <Collapse in={isOpen}>{renderQueryList(list, section.id)}</Collapse>
                </Box>
              );
            })}
          </DragDropContext>
        )}
      </Box>
    </Box>
  );
};

export default QueryFolderSidebar;
